import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import Modal from "react-bootstrap/Modal";
import getCroppedImg from "./cropImage.js";
import useFileUpload from "../../../../hooks/dataSenders/singleUpload.js";

const Nft = ({
  nftFormData,
  setNftFormData,
  nftFormErrors,
  setNftFormErrors,
  setLoader,
}) => {
  // Modal states
  const [show, setShow] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [currentSection, setCurrentSection] = useState(null); // 'nft' or 'buyback'
  const [currentIndex, setCurrentIndex] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const { uploadFile } = useFileUpload();
  const handleShow = () => setShow(true);
  const handleClose = () => {
    setShow(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // Toggle NFT Required
  const toggleNftRequired = () => {
    setNftFormData((prev) => ({
      ...prev,
      nftRequired: !prev.nftRequired,
      nftDetails: !prev.nftRequired ? [{ image: null, price: "" }] : [],
    }));
  };

  // Toggle BuyBack Required
  const toggleBuyBackRequired = () => {
    setNftFormData((prev) => ({
      ...prev,
      buyBackRequired: !prev.buyBackRequired,
      buyBackDetails: !prev.buyBackRequired ? [{ image: null, price: "" }] : [],
    }));
  };

  // Add NFT entry
  const addNftEntry = () => {
    setNftFormData((prev) => ({
      ...prev,
      nftDetails: [...prev.nftDetails, { image: null, price: "" }],
    }));
  };

  // Add BuyBack entry
  const addBuyBackEntry = () => {
    setNftFormData((prev) => ({
      ...prev,
      buyBackDetails: [...prev.buyBackDetails, { image: null, price: "" }],
    }));
  };

  // Delete NFT entry
  const deleteNftEntry = (index) => {
    setNftFormData((prev) => ({
      ...prev,
      nftDetails: prev.nftDetails.filter((_, i) => i !== index),
    }));
  };

  // Delete BuyBack entry
  const deleteBuyBackEntry = (index) => {
    setNftFormData((prev) => ({
      ...prev,
      buyBackDetails: prev.buyBackDetails.filter((_, i) => i !== index),
    }));
  };

  // Handle image upload
  const handleImageUpload = (section, index, file) => {
    if (!file) return;
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      // Set error for this specific section
      const sectionKey = section === "nft" ? "nftDetails" : "buyBackDetails";

      setNftFormErrors((prev) => ({
        ...prev,
        [sectionKey]: `Image ${index + 1}: File size must be less than 5MB`,
      }));
      // Clear the file input
      const inputId =
        section === "nft" ? `nft-input-${index}` : `buyback-input-${index}`;
      const inputElement = document.getElementById(inputId);
      if (inputElement) inputElement.value = "";
      return;
    }

    // ✅ Clear any previous errors for this section
    const sectionKey = section === "nft" ? "nftDetails" : "buyBackDetails";
    setNftFormErrors((prev) => ({
      ...prev,
      [sectionKey]: "",
    }));

    // ✅ NEW: Clear specific item error for image
    setNftFormErrors((prev) => {
      if (prev.nftDetailsItems?.[index]) {
        const updatedItems = [...(prev.nftDetailsItems || [])];
        if (updatedItems[index]) {
          delete updatedItems[index].image;
          // If no more errors for this item, remove the entry
          if (Object.keys(updatedItems[index]).length === 0) {
            updatedItems.splice(index, 1);
          }
        }
        return {
          ...prev,
          nftDetailsItems: updatedItems.length > 0 ? updatedItems : undefined,
        };
      }
      return prev;
    });

    // Continue with existing upload logic
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setImageSrc(reader.result);
      setCurrentSection(section);
      setCurrentIndex(index);
      handleShow();
    };
  };

  // Handle price change
  const handlePriceChange = (section, index, value) => {
    // Allow integers and decimals (e.g., "5", "5.", "5.25", ".5") or empty string
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setNftFormData((prev) => {
        const key = section === "nft" ? "nftDetails" : "buyBackDetails";
        const updated = [...prev[key]];
        updated[index].price = value;
        return { ...prev, [key]: updated };
      });

      // ✅ Clear specific item error for price if value is valid
      if (value !== "" && !isNaN(parseFloat(value)) && parseFloat(value) > 0) {
        setNftFormErrors((prev) => {
          if (prev.nftDetailsItems?.[index]) {
            const updatedItems = [...(prev.nftDetailsItems || [])];
            if (updatedItems[index]) {
              delete updatedItems[index].price;
              // If no more errors for this item, remove the entry
              if (Object.keys(updatedItems[index]).length === 0) {
                updatedItems.splice(index, 1);
              }
            }
            return {
              ...prev,
              nftDetailsItems:
                updatedItems.length > 0 ? updatedItems : undefined,
            };
          }
          return prev;
        });
      }

      // Clear general error
      if (nftFormErrors[section === "nft" ? "nftDetails" : "buyBackDetails"]) {
        setNftFormErrors((prev) => ({
          ...prev,
          [section === "nft" ? "nftDetails" : "buyBackDetails"]: "",
        }));
      }
    }
    // If validation fails, the input is simply not updated (rejected)
  };

  // Delete image
  const handleDeleteImage = (section, index) => {
    setNftFormData((prev) => {
      const key = section === "nft" ? "nftDetails" : "buyBackDetails";
      const updated = [...prev[key]];
      updated[index].image = null;
      return { ...prev, [key]: updated };
    });
  };

  // Crop callbacks
  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const base64ToFile = (base64String, filename = "image.png") => {
    // Remove the data:image/png;base64, part
    const arr = base64String.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const handleSaveCroppedImage = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      const croppedFile = base64ToFile(croppedImage, "cropped-image.png");
      const url = await uploadFile(croppedFile, setLoader);

      setNftFormData((prev) => {
        const key = currentSection === "nft" ? "nftDetails" : "buyBackDetails";
        const updated = [...prev[key]];
        updated[currentIndex].image = url;
        return { ...prev, [key]: updated };
      });

      // ✅ Clear specific item error for image after successful upload
      setNftFormErrors((prev) => {
        if (prev.nftDetailsItems?.[currentIndex]) {
          const updatedItems = [...(prev.nftDetailsItems || [])];
          if (updatedItems[currentIndex]) {
            delete updatedItems[currentIndex].image;
            // If no more errors for this item, remove the entry
            if (Object.keys(updatedItems[currentIndex]).length === 0) {
              updatedItems.splice(currentIndex, 1);
            }
          }
          return {
            ...prev,
            nftDetailsItems: updatedItems.length > 0 ? updatedItems : undefined,
          };
        }
        return prev;
      });

      setZoom(1);
      handleClose();
    } catch (error) {
      console.error("Error cropping image:", error);
      alert("Failed to crop image");
    }
  };

  // Render NFT section
  const renderNftSection = (data, section) => {
    return data.map((item, index) => (
      <div className="boxone" key={index} style={{ position: "relative" }}>
        {/* ... existing code ... */}
        {index !== 0 && (
          <svg
            className="crosssvg"
            onClick={() =>
              section === "nft"
                ? deleteNftEntry(index)
                : deleteBuyBackEntry(index)
            }
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            style={{ cursor: "pointer" }}
          >
            <path
              d="M15 5L5 15"
              stroke="#FC4F52"
              strokeWidth="1.5"
              strokeLinecap="square"
              strokeLinejoin="round"
            />
            <path
              d="M5 5L15 15"
              stroke="#FC4F52"
              strokeWidth="1.5"
              strokeLinecap="square"
              strokeLinejoin="round"
            />
          </svg>
        )}

        <div className="boxes_top">
          <div className="downloddimg">
            {item.image ? (
              <img
                src={item.image}
                alt="preview"
                className="teamPreview"
                onClick={() =>
                  document.getElementById(`${section}-input-${index}`).click()
                }
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  cursor: "pointer",
                }}
              />
            ) : (
              <img
                src="/assets/downlodnew.svg"
                onClick={() =>
                  document.getElementById(`${section}-input-${index}`).click()
                }
                style={{ cursor: "pointer" }}
              />
            )}
          </div>

          <div className="nftimgs">
            <h4>NFT Image</h4>
            <h3 className="elip">
              Images in 1080x1080 resolution. Max 5 MB in JPEG or PNG format
            </h3>

            {item.image ? (
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="replacccebutton"
                  onClick={() =>
                    document.getElementById(`${section}-input-${index}`).click()
                  }
                >
                  Replace
                </button>
                <button
                  className="deletebuttton"
                  onClick={() => {
                    handleDeleteImage(section, index);
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                onClick={() =>
                  document.getElementById(`${section}-input-${index}`).click()
                }
              >
                Upload Image
              </button>
            )}

            <input
              type="file"
              id={`${section}-input-${index}`}
              accept="image/png, image/jpeg"
              hidden
              onChange={(e) => {
                handleImageUpload(section, index, e.target.files[0]);
                e.target.value = "";
              }}
            />

            {/* Show specific image error for this item */}
            {nftFormErrors.nftDetailsItems?.[index]?.image && (
              <span className="error-text">
                {nftFormErrors.nftDetailsItems[index].image}
              </span>
            )}
          </div>
        </div>

        <h6>NFT Price</h6>
        <div className="doller_div">
          <span className="doller">$</span>
          <input
            type="text"
            placeholder="Enter NFT Price"
            value={item.price}
            onChange={(e) => handlePriceChange(section, index, e.target.value)}
          />
        </div>

        {/* Show specific price error for this item */}
        {nftFormErrors.nftDetailsItems?.[index]?.price && (
          <span className="error-text">
            {nftFormErrors.nftDetailsItems[index].price}
          </span>
        )}
      </div>
    ));
  };

  return (
    <>
      <div className="parentsteps">
        <section className="seventhstep">
          {/* NFT Sale Required Toggle */}
          <div>
            <div className="nft_Sales">
              <h3>NFT Sale Required</h3>
              <div className="toggle-container" onClick={toggleNftRequired}>
                <div
                  className={`toggle-circle ${
                    nftFormData.nftRequired ? "active" : ""
                  }`}
                ></div>
              </div>
            </div>

            {nftFormData.nftRequired && (
              <div className="inner_sidenew">
                {renderNftSection(nftFormData.nftDetails, "nft")}

                <button className="addBtn" onClick={addNftEntry}>
                  Add another{" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M5 10H15"
                      stroke="#02171D"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 15V5"
                      stroke="#02171D"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Re-buy Required Toggle */}
          <div>
            <div className="nft_Sales lastsaale">
              <h3>Re-buy Required</h3>
              <div className="toggle-container" onClick={toggleBuyBackRequired}>
                <div
                  className={`toggle-circle ${
                    nftFormData.buyBackRequired ? "active" : ""
                  }`}
                ></div>
              </div>
            </div>

            {/* {nftFormData.buyBackRequired && (
              <div className="inner_sidenew">
                {renderNftSection(nftFormData.buyBackDetails, "buyback")}

                <button className="addBtn" onClick={addBuyBackEntry}>
                  Add another{" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M5 10H15"
                      stroke="#02171D"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 15V5"
                      stroke="#02171D"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )} */}
            {nftFormErrors.buyBackDetails && (
              <span className="error-text">{nftFormErrors.buyBackDetails}</span>
            )}
          </div>
        </section>
      </div>

      {/* Crop Modal */}
      <Modal
        className="publish-modall"
        show={show}
        onHide={handleClose}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Crop Image</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="publish_modal">
            <div className="croopppy">
              <div className="cropmodal">
                {imageSrc && (
                  <div
                    className="crop-container inercrop"
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "240px",
                      background: "#E7EEF2",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      onCropChange={setCrop}
                      onCropComplete={onCropComplete}
                      onZoomChange={setZoom}
                    />
                  </div>
                )}
              </div>
              <p>Drag to reposition image</p>

              <div className="zoom-slider">
                <h5>Zoom</h5>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{
                    width: "100%",
                    height: "8px",
                    borderRadius: "10px",
                    appearance: "none",
                    background: `linear-gradient(to right, #329879 0%, #329879 ${
                      ((zoom - 1) / 2) * 100
                    }%, #d0d7de ${((zoom - 1) / 2) * 100}%, #d0d7de 100%)`,
                    outline: "none",
                  }}
                />
              </div>
              <div className="publish_buttons">
                <button className="cancel_button" onClick={handleClose}>
                  Cancel
                </button>
                <button
                  className="confirm_button"
                  onClick={handleSaveCroppedImage}
                >
                  Save Cropped Image
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Nft;
